/**
 * Fun-ASR语音识别服务（模拟面试专用版本）
 * 基于阿里云DashScope Fun-ASR-Realtime的语音识别服务
 * 专门为模拟面试场景优化，提供低延迟的实时转录
 */

class FunAsrInterviewService {
  constructor() {
    // WebSocket连接必须直接指向后端，不能通过前端代理
    // 使用相对路径，通过Nginx代理访问后端API
    this.baseURL = process.env.REACT_APP_API_BASE_URL || '';
    this.isRunning = false;
    this.isConnected = false;
    this.audioContext = null;
    this.audioStream = null;
    this.audioWorklet = null;
    this.recognitionResults = [];
    this.eventListeners = {};
    
    // WebSocket 相关属性
    this.ws = null;
    this.wsMode = false;
    this.wsReady = false;
    
    // 绑定方法
    this.onResult = this.onResult.bind(this);
    this.onError = this.onError.bind(this);
    this.onStart = this.onStart.bind(this);
    this.onEnd = this.onEnd.bind(this);
  }

  /**
   * 添加事件监听器
   */
  addEventListener(event, callback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  /**
   * 移除事件监听器
   */
  removeEventListener(event, callback) {
    if (this.eventListeners[event]) {
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
    }
  }

  /**
   * 触发事件
   */
  emit(event, data) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(callback => callback(data));
    }
  }

  /**
   * 使用WebSocket方式启动识别（低延迟）
   */
  async startWebSocketRecognition(audioDeviceId = null, maxSentenceSilence = 3000) {
    try {
      // 清理可能的旧连接
      if (this.ws) {
        try { this.ws.close(); } catch (_) {}
        this.ws = null;
      }
      this.wsMode = true;
      this.wsReady = false;
      this.recognitionResults = [];

      // 获取token用于WebSocket认证
      const token = localStorage.getItem('access_token') || '';
      
      // 构造 ws/wss 地址 - 使用模拟面试专用的ASR端点
      // 如果 baseURL 为空，使用当前页面的协议和主机
      let wsBase;
      if (this.baseURL) {
        wsBase = this.baseURL.replace(/^http/, 'ws');
      } else {
        // 使用当前页面的协议和主机
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsBase = `${protocol}//${window.location.host}`;
      }
      // 将token作为query参数传递（WebSocket不支持自定义header）
      const wsUrl = token 
        ? `${wsBase}/api/v1/interview-asr?token=${encodeURIComponent(token)}`
        : `${wsBase}/api/v1/interview-asr`;
      
      const ws = new WebSocket(wsUrl);
      this.ws = ws;

      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          if (!this.wsReady) {
            try { ws.close(); } catch (_) {}
            reject(new Error('WebSocket connection timeout'));
          }
        }, 5000);

        ws.onopen = () => {
          // 初始化消息
          const initMessage = {
            action: 'init',
            language: 'zh-CN',
            max_sentence_silence: maxSentenceSilence
          };
          ws.send(JSON.stringify(initMessage));
        };

        ws.onmessage = async (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (data.type === 'ready') {
              clearTimeout(timeoutId);
              this.isRunning = true;
              this.isConnected = true;
              this.wsReady = true;
              
              // 开始采集音频
              try {
                await this.startAudioCapture(audioDeviceId);
                this.emit('start', { message: 'Fun-ASR语音识别已启动(WS)' });
                resolve(true);
              } catch (err) {
                reject(err);
              }
              return;
            }
            
            if (data.type === 'interim' || data.type === 'final') {
              const isFinal = data.type === 'final';
              const nowTs = data.timestamp ? data.timestamp * 1000 : Date.now();
              
              const latest = {
                text: (data.text || '').trim(),
                timestamp: nowTs,
                is_sentence_end: isFinal,
                confidence: 0.85,
                source: 'paraformer'
              };
              
              // 更新本地缓存
              if (isFinal) {
                if (this.recognitionResults.length > 0 && !this.recognitionResults[this.recognitionResults.length - 1].is_sentence_end) {
                   this.recognitionResults[this.recognitionResults.length - 1] = latest;
                } else {
                   this.recognitionResults.push(latest);
                }
              } else {
                if (this.recognitionResults.length > 0 && !this.recognitionResults[this.recognitionResults.length - 1].is_sentence_end) {
                  this.recognitionResults[this.recognitionResults.length - 1] = latest;
                } else {
                  this.recognitionResults.push(latest);
                }
              }
              
              // 触发结果事件
              this.emit('result', {
                text: latest.text,
                isFinal: isFinal,
                confidence: latest.confidence,
                timestamp: nowTs,
                source: 'fun-asr',
                allResults: this.recognitionResults
              });

              if (isFinal) {
              }
            } else if (data.type === 'error') {
              this.emit('error', { error: data.message });
            }
          } catch (e) {
          }
        };

        ws.onclose = () => {
          this.wsReady = false;
          this.isConnected = false;
          this.isRunning = false;
          this.emit('end', { message: 'Fun-ASR语音识别已停止(WS)' });
        };

        ws.onerror = (err) => {
          if (!this.wsReady) {
             clearTimeout(timeoutId);
             reject(new Error('WebSocket connection error'));
          } else {
             this.emit('error', { error: 'WebSocket 连接失败' });
          }
        };
      });
    } catch (error) {
      this.wsMode = false;
      throw error;
    }
  }

  /**
   * 开始语音识别
   */
  async startRecognition(audioDeviceId = null) {
    try {
      // 使用WebSocket模式 (低延迟)
      const ok = await this.startWebSocketRecognition(audioDeviceId);
      if (!ok) {
        throw new Error('WebSocket启动失败');
      }
      return true;
    } catch (error) {
      this.stopAudioCapture();
      this.emit('error', { error: error.message });
      return false;
    }
  }

  /**
   * 停止语音识别
   */
  async stopRecognition() {
    try {
      // 停止音频捕获
      this.stopAudioCapture();
      
      if (this.ws) {
        try { this.ws.close(); } catch (_) {}
      }
      this.ws = null;
      this.wsMode = false;
      this.wsReady = false;
      
      this.isRunning = false;
      this.isConnected = false;
      this.emit('end', { message: 'Fun-ASR语音识别已停止' });
      
      return true;
    } catch (error) {
      this.emit('error', { error: error.message });
      return false;
    }
  }

  /**
   * 开始音频捕获
   */
  async startAudioCapture(audioDeviceId = null) {
    try {
      // 构建音频约束条件
      const audioConstraints = {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      };
      
      // 如果指定了设备ID，添加设备约束
      if (audioDeviceId && audioDeviceId !== 'default' && audioDeviceId !== '') {
        audioConstraints.deviceId = { exact: audioDeviceId };
        // 对于电脑音频设备，通常需要关闭音频处理
        audioConstraints.echoCancellation = false;
        audioConstraints.noiseSuppression = false;
        audioConstraints.autoGainControl = false;
      } else {
      }
      
      // 获取音频流
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints
      });

      // 创建音频上下文
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000
      });

      // 创建音频源
      const source = this.audioContext.createMediaStreamSource(this.audioStream);
      
      // 使用AudioWorklet或ScriptProcessor
      // 模拟面试使用专门的简单audio-processor，不进行语音增强处理
      if (this.audioContext.audioWorklet) {
        // 使用现代的AudioWorklet（模拟面试专用版本，无增强处理）
        try {
          await this.audioContext.audioWorklet.addModule('/audio-processor-interview.js');
          this.audioWorklet = new AudioWorkletNode(this.audioContext, 'audio-processor');
          
          this.audioWorklet.port.onmessage = (event) => {
            if (this.isRunning && this.isConnected && event.data.audioData) {
              const pcmData = this.floatTo16BitPCM(event.data.audioData);
              this.sendAudioData(pcmData);
            }
          };
          
          source.connect(this.audioWorklet);
          this.audioWorklet.connect(this.audioContext.destination);
        } catch (workletError) {
          this.setupScriptProcessor(source);
        }
      } else {
        this.setupScriptProcessor(source);
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * 设置ScriptProcessor（回退方案）
   */
  setupScriptProcessor(source) {
    // 音频缓冲区 - 减小缓冲区大小，提高响应速度
    this.audioBuffer = [];
    this.bufferSize = 0;
    this.maxBufferSize = 4800; // 300ms的音频数据 (16000 * 0.3)
    
    // 创建音频处理器 - 使用较小的缓冲区
    const processor = this.audioContext.createScriptProcessor(2048, 1, 1);
    
    processor.onaudioprocess = (event) => {
      if (this.isRunning && this.isConnected) {
        const inputBuffer = event.inputBuffer;
        const inputData = inputBuffer.getChannelData(0);
        
        // 检查音频数据是否有效（不是静音）
        const hasAudio = this.hasValidAudio(inputData);
        
        if (hasAudio) {
          // 有有效音频时，将数据添加到缓冲区
          this.audioBuffer.push(new Float32Array(inputData));
          this.bufferSize += inputData.length;
          
          // 当缓冲区达到一定大小时发送数据
          if (this.bufferSize >= this.maxBufferSize) {
            this.flushAudioBuffer();
          }
        } else {
          // 静音时，减少发送频率，只在必要时发送心跳数据
          const now = Date.now();
          if (!this.lastSilenceTime || (now - this.lastSilenceTime) > 2000) { // 每2秒发送一次静音心跳
            this.sendSilenceHeartbeat();
            this.lastSilenceTime = now;
          }
        }
      }
    };
    
    source.connect(processor);
    processor.connect(this.audioContext.destination);
    
    this.processor = processor;
  }
  
  /**
   * 检查音频数据是否包含有效音频（非静音）
   */
  hasValidAudio(audioData) {
    const threshold = 0.005; // 降低音频阈值，提高敏感度
    let maxAmplitude = 0;
    
    // 计算最大振幅
    for (let i = 0; i < audioData.length; i++) {
      const amplitude = Math.abs(audioData[i]);
      if (amplitude > maxAmplitude) {
        maxAmplitude = amplitude;
      }
    }
    
    // 如果最大振幅超过阈值，认为有有效音频
    return maxAmplitude > threshold;
  }

  /**
   * 刷新音频缓冲区
   */
  flushAudioBuffer() {
    if (this.audioBuffer.length === 0) return;
    
    // 合并所有缓冲的音频数据
    const totalLength = this.audioBuffer.reduce((sum, buffer) => sum + buffer.length, 0);
    const combinedBuffer = new Float32Array(totalLength);
    
    let offset = 0;
    for (const buffer of this.audioBuffer) {
      combinedBuffer.set(buffer, offset);
      offset += buffer.length;
    }
    
    // 转换为PCM数据并发送
    const pcmData = this.floatTo16BitPCM(combinedBuffer);
    this.sendAudioData(pcmData);
    
    // 清空缓冲区
    this.audioBuffer = [];
    this.bufferSize = 0;
  }

  /**
   * 发送静音心跳数据（保持长连接）
   */
  sendSilenceHeartbeat() {
    try {
      // 生成静音音频数据（100ms的静音）
      const silenceDuration = 0.1; // 100ms
      const sampleCount = Math.floor(16000 * silenceDuration); // 1600个样本
      const silenceData = new Float32Array(sampleCount); // 全零数组，表示静音
      
      const pcmData = this.floatTo16BitPCM(silenceData);
      
      this.sendAudioData(pcmData);
    } catch (error) {
    }
  }

  /**
   * 停止音频捕获
   */
  stopAudioCapture() {
    try {
      // 刷新剩余的音频缓冲区
      if (this.audioBuffer && this.audioBuffer.length > 0) {
        this.flushAudioBuffer();
      }
      
      if (this.audioWorklet) {
        this.audioWorklet.disconnect();
        this.audioWorklet = null;
      }
      
      if (this.processor) {
        this.processor.disconnect();
        this.processor = null;
      }
      
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop());
        this.audioStream = null;
      }
      
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close();
        this.audioContext = null;
      }
      
      this.audioBuffer = [];
      this.bufferSize = 0;
    } catch (error) {
    }
  }

  /**
   * 发送音频数据到后端
   */
  async sendAudioData(pcmData) {
    try {
      if (!pcmData || pcmData.byteLength === 0) {
        return;
      }
      
      if (this.wsMode && this.ws && this.wsReady && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(pcmData);
          return;
        } catch (wsError) {
          return;
        }
      }
      
    } catch (error) {
    }
  }

  // WebSocket模式无需轮询，结果通过WebSocket实时接收

  /**
   * 获取识别结果
   */
  getResults() {
    return this.recognitionResults;
  }

  /**
   * 清空识别结果
   */
  async clearResults() {
    this.recognitionResults = [];
  }

  /**
   * 获取服务状态
   */
  async getStatus() {
    return {
      is_running: this.isRunning,
      is_connected: this.isConnected,
      results_count: this.recognitionResults.length
    };
  }

  /**
   * 将Float32Array转换为16位PCM数据
   */
  floatTo16BitPCM(input) {
    // 确保输入数据有效
    if (!input || input.length === 0) {
      return new ArrayBuffer(0);
    }
    
    const buffer = new ArrayBuffer(input.length * 2);
    const view = new DataView(buffer);
    let offset = 0;
    
    for (let i = 0; i < input.length; i++, offset += 2) {
      // 限制范围到[-1, 1]
      const s = Math.max(-1, Math.min(1, input[i]));
      // 转换为16位整数，使用小端序
      const sample = s < 0 ? s * 0x8000 : s * 0x7FFF;
      view.setInt16(offset, sample, true);
    }
    
    return buffer;
  }

  /**
   * 处理识别结果
   */
  onResult(data) {
    this.emit('result', data);
  }

  /**
   * 处理错误
   */
  onError(error) {
    this.emit('error', error);
  }

  /**
   * 处理开始事件
   */
  onStart(data) {
    this.emit('start', data);
  }

  /**
   * 处理结束事件
   */
  onEnd(data) {
    this.emit('end', data);
  }

  /**
   * 销毁服务
   */
  destroy() {
    this.stopRecognition();
    this.eventListeners = {};
  }
}

export default FunAsrInterviewService;

